import _ from "lodash";
import { getExpandedFields, isHidePinCodeGatedShowIf, resolveFieldPath, shouldShowField } from "../../../../components/InputField/helper";

const LEGACY_TO_CANONICAL_FIELD_MAP = {
    Business_Plan_Disclosure: "hasBusinessPlan",
    entityType: "Entity_Type",
    Count_Of_Monthly_Stablecoin_Conversions_Deposits: "Number_Of_Monthly_Stablecoin_Conversions_Deposits",
    Count_Of_Monthly_Stablecoin_Conversions_Payments: "Number_Of_Monthly_Stablecoin_Conversions_Payments",
    Expected_Amount_Monthly_Incoming_USDC: "Amount_Of_Monthly_Stablecoin_Conversions_Deposits",
    Expected_Amount_Monthly_Outgoing_USDC: "Amount_Of_Monthly_Stablecoin_Conversions_Payments",
};

const CANONICAL_TO_LEGACY_FIELD_MAP = Object.entries(LEGACY_TO_CANONICAL_FIELD_MAP)
    .reduce((accumulator, [legacyField, canonicalField]) => {
        accumulator[canonicalField] = legacyField;
        return accumulator;
    }, {});

const ARRAY_API_COMPATIBILITY_FIELDS = new Set(["businesses"]);
const NESTED_HELPER_ONLY_FIELDS = new Set(["hidePinCode", "_localId", "_isNew"]);
const INDIVIDUAL_METADATA_FIELDS = new Set([
    "_id",
    "_localId",
    "_isNew",
    "aEId",
    "cyclosUserId",
    "cyclosUserName",
    "application_id",
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "version",
    "status",
    "locked",
    "hidePinCode",
]);
const ENTITY_METADATA_FIELDS = new Set([
    "_id",
    "_localId",
    "_isNew",
    "aEId",
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "version",
    "status",
    "hidePinCode",
]);

const normalizeIndexedFieldPath = (fieldPath = "") => String(fieldPath)
    .replace(/\[(\d+)\]/g, "[]")
    .replace(/\.([0-9]+)(?=\.|$)/g, "[]");

const hasMeaningfulPrimitiveValue = (value) => {
    if (value === null || value === undefined) {
        return false;
    }

    if (typeof value === "string") {
        return value.trim().length > 0;
    }

    if (typeof value === "number") {
        return Number.isFinite(value);
    }

    if (typeof value === "boolean") {
        return value === true;
    }

    return false;
};

const hasMeaningfulNestedValue = (value) => {
    if (Array.isArray(value)) {
        return value.some((item) => hasMeaningfulNestedValue(item));
    }

    if (_.isPlainObject(value)) {
        return Object.entries(value).some(([key, item]) => (
            !NESTED_HELPER_ONLY_FIELDS.has(key) && hasMeaningfulNestedValue(item)
        ));
    }

    return hasMeaningfulPrimitiveValue(value);
};

const hasMeaningfulIndividualValue = (key, value) => {
    if (INDIVIDUAL_METADATA_FIELDS.has(key)) {
        return false;
    }

    if (key === "isPrimaryUser" || key === "isAuthorizedUser" || key === "accountOpeningWorkflowDriven") {
        return value === true ? false : hasMeaningfulPrimitiveValue(value);
    }

    if (key === "accessType") {
        return typeof value === "string" && value.trim().length > 0 && value !== "NoGroup";
    }

    return hasMeaningfulNestedValue(value);
};

const hasMeaningfulEntityValue = (key, value) => {
    if (ENTITY_METADATA_FIELDS.has(key)) {
        return false;
    }

    return hasMeaningfulNestedValue(value);
};

const isDraftPlaceholderIndividual = (individual) => {
    if (!_.isPlainObject(individual)) {
        return false;
    }

    if (individual?._id || individual?.aEId || individual?.cyclosUserId || individual?.cyclosUserName) {
        return false;
    }

    return !Object.entries(individual).some(([key, value]) => hasMeaningfulIndividualValue(key, value));
};

const isDraftPlaceholderEntity = (entity) => {
    if (!_.isPlainObject(entity)) {
        return false;
    }

    if (entity?._id || entity?.aEId) {
        return false;
    }

    return !Object.entries(entity).some(([key, value]) => hasMeaningfulEntityValue(key, value));
};

const stripDraftPlaceholderRowsForApi = (payload) => {
    if (!_.isPlainObject(payload)) {
        return payload;
    }

    const nextPayload = _.cloneDeep(payload);

    if (Array.isArray(nextPayload.individuals)) {
        nextPayload.individuals = nextPayload.individuals.filter((individual) => !isDraftPlaceholderIndividual(individual));
    }

    if (Array.isArray(nextPayload.entities)) {
        nextPayload.entities = nextPayload.entities.filter((entity) => !isDraftPlaceholderEntity(entity));
    }

    return nextPayload;
};

const remapRootFields = (payload, mapping) => {
    if (!_.isPlainObject(payload)) {
        return payload;
    }

    const nextPayload = _.cloneDeep(payload);
    Object.entries(mapping).forEach(([fromField, toField]) => {
        if (!Object.prototype.hasOwnProperty.call(nextPayload, fromField)) {
            return;
        }

        nextPayload[toField] = _.cloneDeep(nextPayload[fromField]);
        delete nextPayload[fromField];
    });

    return nextPayload;
};

const remapFieldPath = (fieldPath, mapping) => {
    if (!fieldPath) {
        return fieldPath;
    }

    const normalizedFieldPath = String(fieldPath);
    for (const [fromField, toField] of Object.entries(mapping)) {
        if (normalizedFieldPath === fromField) {
            return toField;
        }
        if (normalizedFieldPath.startsWith(`${fromField}.`) || normalizedFieldPath.startsWith(`${fromField}[`)) {
            return `${toField}${normalizedFieldPath.slice(fromField.length)}`;
        }
    }

    return normalizedFieldPath;
};

const findLegacyMappingForFieldPath = (fieldPath) => {
    const normalizedFieldPath = String(fieldPath ?? "");
    return Object.entries(LEGACY_TO_CANONICAL_FIELD_MAP).find(([legacyField]) => (
        normalizedFieldPath === legacyField
        || normalizedFieldPath.startsWith(`${legacyField}.`)
        || normalizedFieldPath.startsWith(`${legacyField}[`)
    )) || null;
};

const getFieldDefinitions = (fieldDefinitions) => {
    if (Array.isArray(fieldDefinitions)) {
        return fieldDefinitions.filter((fieldDefinition) => _.isPlainObject(fieldDefinition));
    }

    if (_.isPlainObject(fieldDefinitions)) {
        return Object.values(fieldDefinitions).filter((fieldDefinition) => _.isPlainObject(fieldDefinition));
    }

    return [];
};

const toLodashPath = (fieldPath = "") => String(fieldPath || "").replace(/\[(\d+)\]/g, ".$1");

const hasFieldPath = (payload, fieldPath = "") => {
    if (!fieldPath) {
        return false;
    }

    return _.has(payload, toLodashPath(fieldPath));
};

const getCompatibleFieldPaths = (fieldPath = "") => {
    const normalizedFieldPath = String(fieldPath || "");
    const compatibleFieldPaths = new Set([normalizedFieldPath]);
    const canonicalFieldPath = remapFieldPath(normalizedFieldPath, LEGACY_TO_CANONICAL_FIELD_MAP);
    const legacyFieldPath = remapFieldPath(normalizedFieldPath, CANONICAL_TO_LEGACY_FIELD_MAP);

    if (canonicalFieldPath) {
        compatibleFieldPaths.add(canonicalFieldPath);
    }
    if (legacyFieldPath) {
        compatibleFieldPaths.add(legacyFieldPath);
    }

    return [...compatibleFieldPaths].filter(Boolean);
};

const getHiddenFieldPathsToClear = (visibilityPayload, fieldDefinitions, sourcePayload = visibilityPayload) => {
    if (!_.isPlainObject(visibilityPayload)) {
        return [];
    }

    const definitions = getFieldDefinitions(fieldDefinitions);
    if (!definitions.length) {
        return [];
    }

    const fieldsToClear = new Set();

    getExpandedFields(definitions, visibilityPayload).forEach((fieldDefinition) => {
        if (!fieldDefinition?.field || !fieldDefinition?.showIf) {
            return;
        }

        // A postal-code field hidden via its `hidePinCode` flag (country with a fixed default, e.g.
        // HK -> 999077) was just auto-filled with an authoritative value that must persist. Clearing
        // it here would null the auto-populated pinCode on save - the same defect the WrapperField
        // visibility reset was fixed for - so exempt hidePinCode-gated fields from the clear set.
        if (isHidePinCodeGatedShowIf(fieldDefinition.showIf)) {
            return;
        }

        const arrayIndexMatch = typeof fieldDefinition.field === "string"
            ? fieldDefinition.field.match(/\[(\d+)\]/)
            : null;
        const arrayIndexing = arrayIndexMatch ? Number(arrayIndexMatch[1]) : null;

        if (shouldShowField(visibilityPayload, fieldDefinition.showIf, arrayIndexing)) {
            return;
        }

        const resolvedFieldPath = resolveFieldPath(fieldDefinition.field, arrayIndexing);
        getCompatibleFieldPaths(resolvedFieldPath).forEach((fieldPath) => {
            if (hasFieldPath(sourcePayload, fieldPath)) {
                fieldsToClear.add(fieldPath);
            }
        });
    });

    return [...fieldsToClear];
};

export const unsetHiddenBusinessV2Fields = (payload, fieldDefinitions = null) => {
    if (!_.isPlainObject(payload)) {
        return {
            payload,
            clearedFieldPaths: [],
        };
    }

    const nextPayload = _.cloneDeep(payload);
    const clearedFieldPaths = getHiddenFieldPathsToClear(nextPayload, fieldDefinitions);

    clearedFieldPaths.forEach((fieldPath) => {
        _.unset(nextPayload, toLodashPath(fieldPath));
    });

    return {
        payload: nextPayload,
        clearedFieldPaths,
    };
};

const buildFieldDefinitionMap = (fieldDefinitions) => getFieldDefinitions(fieldDefinitions).reduce((accumulator, fieldDefinition) => {
    const normalizedFieldPath = normalizeIndexedFieldPath(
        remapFieldPath(fieldDefinition?.field, LEGACY_TO_CANONICAL_FIELD_MAP)
    );

    if (!normalizedFieldPath || accumulator[normalizedFieldPath]) {
        return accumulator;
    }

    accumulator[normalizedFieldPath] = fieldDefinition;
    return accumulator;
}, {});

const coerceBooleanValue = (value) => {
    if (value === null || value === undefined || value === "" || typeof value === "boolean") {
        return value;
    }

    if (typeof value === "string") {
        const normalizedValue = value.trim().toLowerCase();
        if (normalizedValue === "true" || normalizedValue === "1") return true;
        if (normalizedValue === "false" || normalizedValue === "0") return false;
        return value;
    }

    if (typeof value === "number") {
        if (value === 1) return true;
        if (value === 0) return false;
    }

    return value;
};

const coerceNumberValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return value;
    }

    if (typeof value === "number") {
        return Number.isFinite(value) ? value : value;
    }

    if (typeof value === "string") {
        const normalizedValue = value.trim();
        if (!normalizedValue) {
            return value;
        }

        const coercedValue = Number(normalizedValue);
        return Number.isFinite(coercedValue) ? coercedValue : value;
    }

    if (typeof value === "boolean") {
        return value ? 1 : 0;
    }

    return value;
};

const coerceStringValue = (value) => {
    if (value === null || value === undefined || typeof value === "string") {
        return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    return value;
};

const coerceValueForFieldDefinition = (value, fieldDefinition) => {
    switch (fieldDefinition?.type) {
        case "string":
            return coerceStringValue(value);
        case "number":
        case "integer":
            return coerceNumberValue(value);
        case "boolean":
            return coerceBooleanValue(value);
        default:
            return value;
    }
};

const applyFieldCoercionToPayload = (payloadNode, fieldPath, fieldDefinition) => {
    if (!fieldPath) {
        return payloadNode;
    }

    const [currentSegment, ...restSegments] = String(fieldPath).split(".");
    const isArraySegment = currentSegment.endsWith("[]");
    const fieldKey = currentSegment.replace(/\[\]$/, "");

    if (!payloadNode || typeof payloadNode !== "object") {
        return payloadNode;
    }

    if (isArraySegment) {
        if (!Array.isArray(payloadNode[fieldKey])) {
            return payloadNode;
        }

        payloadNode[fieldKey] = payloadNode[fieldKey].map((item) => (
            restSegments.length
                ? applyFieldCoercionToPayload(item, restSegments.join("."), fieldDefinition)
                : coerceValueForFieldDefinition(item, fieldDefinition)
        ));
        return payloadNode;
    }

    if (!Object.prototype.hasOwnProperty.call(payloadNode, fieldKey)) {
        return payloadNode;
    }

    if (!restSegments.length) {
        payloadNode[fieldKey] = coerceValueForFieldDefinition(payloadNode[fieldKey], fieldDefinition);
        return payloadNode;
    }

    payloadNode[fieldKey] = applyFieldCoercionToPayload(payloadNode[fieldKey], restSegments.join("."), fieldDefinition);
    return payloadNode;
};

const coercePayloadUsingFieldDefinitions = (payload, fieldDefinitions) => {
    if (!_.isPlainObject(payload)) {
        return payload;
    }

    const normalizedPayload = remapRootFields(payload, LEGACY_TO_CANONICAL_FIELD_MAP);
    const nextPayload = _.cloneDeep(normalizedPayload);
    const fieldDefinitionMap = buildFieldDefinitionMap(fieldDefinitions);

    Object.entries(fieldDefinitionMap).forEach(([fieldPath, fieldDefinition]) => {
        applyFieldCoercionToPayload(nextPayload, fieldPath, fieldDefinition);
    });

    return nextPayload;
};

const coerceFieldValueMapEntry = (fieldPath, value, fieldDefinitionMap) => {
    const normalizedFieldPath = normalizeIndexedFieldPath(
        remapFieldPath(fieldPath, LEGACY_TO_CANONICAL_FIELD_MAP)
    );
    const fieldDefinition = fieldDefinitionMap[normalizedFieldPath];
    return fieldDefinition ? coerceValueForFieldDefinition(value, fieldDefinition) : value;
};

const normalizeCompatibilityValueForApi = (fieldPath, value) => {
    if (!ARRAY_API_COMPATIBILITY_FIELDS.has(fieldPath)) {
        return value;
    }

    if (Array.isArray(value)) {
        return _.cloneDeep(value);
    }

    if (value === null || value === undefined || value === "") {
        return [];
    }

    return [_.cloneDeep(value)];
};

const hydrateCompatibilityValueFromApi = (fieldPath, value) => {
    if (!ARRAY_API_COMPATIBILITY_FIELDS.has(fieldPath)) {
        return value;
    }

    if (Array.isArray(value)) {
        return _.cloneDeep(value);
    }

    if (value === null || value === undefined || value === "") {
        return [];
    }

    return [_.cloneDeep(value)];
};

const applyCompatibilityPayloadForApi = (payload) => {
    if (!_.isPlainObject(payload)) {
        return payload;
    }

    const nextPayload = _.cloneDeep(payload);
    ARRAY_API_COMPATIBILITY_FIELDS.forEach((fieldPath) => {
        if (!Object.prototype.hasOwnProperty.call(nextPayload, fieldPath)) {
            return;
        }
        nextPayload[fieldPath] = normalizeCompatibilityValueForApi(fieldPath, nextPayload[fieldPath]);
    });

    return nextPayload;
};

const applyCompatibilityPayloadFromApi = (payload) => {
    if (!_.isPlainObject(payload)) {
        return payload;
    }

    const nextPayload = _.cloneDeep(payload);
    ARRAY_API_COMPATIBILITY_FIELDS.forEach((fieldPath) => {
        if (!Object.prototype.hasOwnProperty.call(nextPayload, fieldPath)) {
            return;
        }
        nextPayload[fieldPath] = hydrateCompatibilityValueFromApi(fieldPath, nextPayload[fieldPath]);
    });

    return nextPayload;
};

export const normalizeBusinessV2PayloadForApi = (payload, fieldDefinitions = null) => {
    const coercedPayload = coercePayloadUsingFieldDefinitions(payload, fieldDefinitions);
    const { payload: sanitizedPayload } = unsetHiddenBusinessV2Fields(coercedPayload, fieldDefinitions);

    return applyCompatibilityPayloadForApi(
        stripDraftPlaceholderRowsForApi(sanitizedPayload)
    );
};

export const hydrateBusinessV2PayloadFromApi = (payload) => {
    if (!_.isPlainObject(payload)) {
        return payload;
    }

    const nextPayload = _.cloneDeep(payload);
    Object.entries(LEGACY_TO_CANONICAL_FIELD_MAP).forEach(([legacyField, canonicalField]) => {
        const hasLegacyField = Object.prototype.hasOwnProperty.call(nextPayload, legacyField);
        const hasCanonicalField = Object.prototype.hasOwnProperty.call(nextPayload, canonicalField);

        if (hasLegacyField && !hasCanonicalField) {
            nextPayload[canonicalField] = _.cloneDeep(nextPayload[legacyField]);
        }

        if (hasCanonicalField && !hasLegacyField) {
            nextPayload[legacyField] = _.cloneDeep(nextPayload[canonicalField]);
        }
    });

    return applyCompatibilityPayloadFromApi(nextPayload);
};

export const normalizeBusinessV2FieldValueMapForApi = (fieldValueMap = {}, fieldDefinitions = null, currentPayload = null, previousPayload = null) => {
    if (!fieldValueMap || typeof fieldValueMap !== "object") {
        return fieldValueMap;
    }

    const normalizedFieldValueMap = {};
    const fieldDefinitionMap = buildFieldDefinitionMap(fieldDefinitions);
    Object.entries(fieldValueMap).forEach(([fieldPath, value]) => {
        if (findLegacyMappingForFieldPath(fieldPath)) {
            return;
        }
        normalizedFieldValueMap[fieldPath] = _.cloneDeep(
            normalizeCompatibilityValueForApi(
                fieldPath,
                coerceFieldValueMapEntry(fieldPath, value, fieldDefinitionMap)
            )
        );
    });

    Object.entries(fieldValueMap).forEach(([fieldPath, value]) => {
        const normalizedFieldPath = remapFieldPath(fieldPath, LEGACY_TO_CANONICAL_FIELD_MAP);
        if (normalizedFieldPath !== fieldPath) {
            normalizedFieldValueMap[normalizedFieldPath] = _.cloneDeep(
                normalizeCompatibilityValueForApi(
                    normalizedFieldPath,
                    coerceFieldValueMapEntry(normalizedFieldPath, value, fieldDefinitionMap)
                )
            );
        }
    });

    getHiddenFieldPathsToClear(currentPayload, fieldDefinitions, previousPayload || currentPayload).forEach((fieldPath) => {
        normalizedFieldValueMap[fieldPath] = null;
    });

    return normalizedFieldValueMap;
};

export const hydrateBusinessV2FieldValueMapFromApi = (fieldValueMap = {}) => {
    if (!fieldValueMap || typeof fieldValueMap !== "object") {
        return fieldValueMap;
    }

  const hydratedFieldValueMap = Object.entries(fieldValueMap).reduce((accumulator, [fieldPath, value]) => {
    accumulator[fieldPath] = _.cloneDeep(
      hydrateCompatibilityValueFromApi(fieldPath, value)
    );
        return accumulator;
    }, {});

  Object.entries(fieldValueMap).forEach(([fieldPath, value]) => {
    const hydratedLegacyFieldPath = remapFieldPath(fieldPath, CANONICAL_TO_LEGACY_FIELD_MAP);
    if (hydratedLegacyFieldPath !== fieldPath && !Object.prototype.hasOwnProperty.call(hydratedFieldValueMap, hydratedLegacyFieldPath)) {
      hydratedFieldValueMap[hydratedLegacyFieldPath] = _.cloneDeep(
                hydrateCompatibilityValueFromApi(hydratedLegacyFieldPath, value)
            );
        }

        const hydratedCanonicalFieldPath = remapFieldPath(fieldPath, LEGACY_TO_CANONICAL_FIELD_MAP);
        if (hydratedCanonicalFieldPath !== fieldPath && !Object.prototype.hasOwnProperty.call(hydratedFieldValueMap, hydratedCanonicalFieldPath)) {
            hydratedFieldValueMap[hydratedCanonicalFieldPath] = _.cloneDeep(
                hydrateCompatibilityValueFromApi(hydratedCanonicalFieldPath, value)
            );
        }
    });

    return hydratedFieldValueMap;
};
